import {useId} from "react";
import {FormGroup} from "@/shared/ui/form/FormGroup";
import {ComboboxInput} from "@/shared/ui/primitives/ComboboxInput";
import {useNoticeUserOptions, type UserOption} from "@/features/requests/workingTimeNoticeDialog/useNoticeUserOptions";

interface WorkingTimeNoticeUserComboBoxFormGroupProps {
	value: UserOption | null;
	onValueChange: (user: UserOption | null) => void;
	/** Renders the combo box non-interactive (e.g. a read-only dialog). */
	disabled?: boolean;
}

const formatUserOption = (u: UserOption) => u.fullname;

export function WorkingTimeNoticeUserComboBoxFormGroup({
	value,
	onValueChange,
	disabled = false
}: WorkingTimeNoticeUserComboBoxFormGroupProps) {
	const inputId = useId();
	const {users, loading, error} = useNoticeUserOptions();

	// `emptyMessage` is what the combo box shows when nothing is selected — i.e. a placeholder, not a
	// "no results" message — so the resting state is a prompt to pick a user, not "no users found".
	const emptyMessage = loading
		? messages["combobox.user.loading"]
		: error
			? messages["combobox.user.error.load"]
			: messages["combobox.user.placeholder"];

	return (
		<FormGroup label={messages["workingTimeNotice.user"]} htmlFor={inputId} labelColClass="col-xs-3" inputColClass="col-xs-9">
			<ComboboxInput
				id={inputId}
				value={value}
				items={users}
				onValueChange={onValueChange}
				formatter={formatUserOption}
				disabled={disabled || loading}
				emptyMessage={emptyMessage}
			/>
		</FormGroup>
	);
}
